import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { supabase, isSupabaseConfigured, signInWithAccessToken } from '../lib/supabase';
import type { AppUser, UserRole } from '../types/database';
export interface User {
  id?: string;
  email: string;
  name?: string;
  territorio?: string;
  horario?: any;
  role?: UserRole;
  accessToken?: string;
  calendarConnected?: boolean;
  emailConnected?: boolean;
}
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  needsProfileSetup: boolean;
  loading: boolean;
  sendMagicLink: (email: string) => Promise<void>;
  verifyMagic: (token: string) => Promise<void>;
  sendOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, code: string) => Promise<void>;
  loginWithAccessToken: (token: string) => Promise<void>;
  completeProfile: (data: Partial<User>) => Promise<void>;
  logout: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
const appUserToUser = (u: AppUser): User => ({
  id: u.id,
  email: u.email,
  name: u.full_name ?? undefined,
  territorio: u.territory ?? undefined,
  horario: u.horario ?? undefined,
  role: u.role,
  accessToken: u.access_token,
  calendarConnected: u.calendar_connected,
  emailConnected: u.email_connected
});
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [user, setUser] = useState<User | null>(() => {
    if (isSupabaseConfigured) return null;
    const saved = localStorage.getItem('growthly_auth');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const loadAppUser = useCallback(async (userId: string, fallbackEmail: string) => {
    if (!supabase) return;
    const {
      data
    } = await supabase.from('users').select('*').eq('id', userId).maybeSingle();
    if (data) {
      setUser(appUserToUser(data as AppUser));
    } else {
      // Auto-trigger should have created the row; if not, do it now.
      const {
        data: inserted
      } = await supabase.from('users').insert({
        id: userId,
        email: fallbackEmail
      }).select().maybeSingle();
      if (inserted) setUser(appUserToUser(inserted as AppUser));else setUser({
        id: userId,
        email: fallbackEmail
      });
    }
  }, []);
  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setLoading(false);
      return;
    }
    supabase.auth.getSession().then(async ({
      data: {
        session
      }
    }) => {
      if (session?.user) await loadAppUser(session.user.id, session.user.email ?? '');
      setLoading(false);
    });
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) await loadAppUser(session.user.id, session.user.email ?? '');else setUser(null);
    });
    return () => subscription.unsubscribe();
  }, [loadAppUser]);
  useEffect(() => {
    if (isSupabaseConfigured) return;
    if (user) localStorage.setItem('growthly_auth', JSON.stringify(user));else localStorage.removeItem('growthly_auth');
  }, [user]);
  const isAuthenticated = !!user;
  const needsProfileSetup = isAuthenticated && !user?.name;
  const sendMagicLink = async (email: string) => {
    if (!isSupabaseConfigured || !supabase) {
      await new Promise(r => setTimeout(r, 600));
      return;
    }
    const {
      error
    } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/app/dashboard`
      }
    });
    if (error) throw error;
  };
  const verifyMagic = async (_token: string) => {
    if (!isSupabaseConfigured || !supabase) {
      await new Promise(r => setTimeout(r, 600));
      setUser({
        email: 'demo@growthly.com'
      });
    }
    // Real magic link is auto-handled by detectSessionInUrl
  };
  const sendOtp = async (email: string) => {
    if (!isSupabaseConfigured || !supabase) {
      await new Promise(r => setTimeout(r, 600));
      return;
    }
    const {
      error
    } = await supabase.auth.signInWithOtp({
      email
    });
    if (error) throw error;
  };
  const verifyOtp = async (email: string, code: string) => {
    if (!isSupabaseConfigured || !supabase) {
      await new Promise(r => setTimeout(r, 600));
      if (code !== '123456') throw new Error('Código inválido');
      setUser({
        email
      });
      return;
    }
    const {
      error
    } = await supabase.auth.verifyOtp({
      email,
      token: code,
      type: 'email'
    });
    if (error) throw new Error(error.message);
  };
  const loginWithAccessToken = async (token: string) => {
    if (!isSupabaseConfigured || !supabase) {
      await new Promise(r => setTimeout(r, 600));
      setUser({
        email: 'setter@growthly.com',
        accessToken: token,
        name: 'Demo Setter',
        role: 'setter'
      });
      return;
    }
    const res = await signInWithAccessToken(token);
    if (!res.ok) throw new Error(res.error || 'Token inválido');
    // signInWithAccessToken navigates away on success
  };
  const completeProfile = async (data: Partial<User>) => {
    if (!isSupabaseConfigured || !supabase) {
      await new Promise(r => setTimeout(r, 600));
      setUser(prev => prev ? {
        ...prev,
        ...data
      } : null);
      return;
    }
    const {
      data: {
        user: authUser
      }
    } = await supabase.auth.getUser();
    if (!authUser) throw new Error('No authenticated user');
    const {
      data: updated,
      error
    } = await supabase.from('users').update({
      full_name: data.name ?? null,
      territory: data.territorio ?? null,
      horario: data.horario ?? null,
      calendar_connected: data.calendarConnected ?? false,
      email_connected: data.emailConnected ?? false
    }).eq('id', authUser.id).select().single();
    if (error) throw error;
    setUser(appUserToUser(updated as AppUser));
  };
  const logout = async () => {
    if (!isSupabaseConfigured || !supabase) {
      setUser(null);
      return;
    }
    await supabase.auth.signOut();
    setUser(null);
  };
  return <AuthContext.Provider value={{
    user,
    isAuthenticated,
    needsProfileSetup,
    loading,
    sendMagicLink,
    verifyMagic,
    sendOtp,
    verifyOtp,
    loginWithAccessToken,
    completeProfile,
    logout
  }} data-id="element-591">
      {children}
    </AuthContext.Provider>;
}
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};